import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {property} from 'lit/decorators/property.js';
import {msg} from '@lit/localize';

import {connect} from 'pwa-helpers';
import store from '../../../store';
import {
	selectShowPersonalisation,
	selectSpcAttributes,
	selectSpcConfiguration,
	selectSpcIsSatisfied,
	selectSpcSession,
	selectSpcSessions
} from '../../../selectors';
import {
	selectGroupedHeads,
	selectIomoDesign,
	selectIomoDesignIsPole,
	selectIomoHeads
} from '../../iomoSelectors';
import {getValueOfFeature, spcExpliciteChoiceDecision} from '../../../util/values';
import {fetchAll} from '../../../slices/productDataSlice';
import {fetchShortNumber} from '../../../slices/shortNumberSlice';
import {spcToStoredDecisions} from '../../../components/spc/util/spcUtils';
import {getFilteredDecisions} from '../../../util/getFilteredDecisions';
import {setSection} from '../../../slices/statusSlice';

@customElement('iomo-specsheet')
export class IomoSpecsheet extends connect(store)(LitElement) {

	@property({type: String})
	type = 'detail'; // family, basic, detail

	typeLabels = {
		family: msg('Series spec sheet'),
		basic: msg('Basic spec sheet'),
		detail: msg('Detail spec sheet')
	};

	static styles = [
		css`
			:host {
				container-type: inline-size;
			}

			@media screen {
				.page {
					max-width: 1400px;
					width: 90%;
					box-sizing: border-box;
					margin: 40px auto 20px auto;
					box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.25);
				}
			}
			@media print {
				.page {
					margin: 0;
					box-shadow: none;
				}
			}
		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
		this.groupsAvailable = false;
		this.configurationReady = false;
	}

	stateChanged(state) {
		this.state = state;
		this.loggedIn = state.userProfile.data.loggedIn;

		this.spcConfiguration = selectSpcConfiguration(state);

		if (this.spcConfiguration) {
			this.spcAttributes = selectSpcAttributes(state);
			this.spcIsSatisfied = selectSpcIsSatisfied(state);

			if (state.specsheet.data.specsheets && state.status?.productFamily) {
				this.kirbySpecsheets = state.specsheet.data.specsheets;
				this.family = state.status.productFamily;
				//if (this.kirbySpecsheets.length > 0) {
					//this.kirbySpecsheet = this.kirbySpecsheets?.find(specsheet => specsheet.id === this.family);
				//}
			}
			console.log('2', this.spcAttributes);

			// group heads on pages based on properties
			this.groupedHeads = selectGroupedHeads(state);
			this.baseHeadAttributes = this.spcAttributes
				.map(attribute => ({id: attribute.key, value: getValueOfFeature(attribute.key, this.spcConfiguration)}))
				.filter(attribute => (
					attribute.id !== 'root::HS_HousingSize' &&
					attribute.id !== 'root::LE_ColourTemperature_1' &&
					attribute.id !== 'root::LE_LightDistribution_1' &&
					attribute.id !== 'root::LU_LegacyArticleNumberGerman' &&
					attribute.id !== 'root::LE_AccessoriesSuffix_1' &&
					attribute.id !== 'root::LE_DimmingLevel_1' &&
					attribute.id !== 'root::HS_HousingPowerConnection'
				))
				.filter(attribute => attribute.value !== null);
			if (!this.groupsAvailable && this.groupedHeads?.length > 0 && this.baseHeadAttributes.length > 15) {
				// TODO: properly detect readyness of base config
				this.groupsAvailable = true;
				this.groupedHeads.forEach((group, index) => this.startSpcSession(group, group.instanceId));
			}
		}
		if (!this.configurationReady && this.state.status.initialized) {
			this.configurationReady = true;
			this.getShortNumber();
		}
		this.showPersonalisation = selectShowPersonalisation(state);
	}

	firstUpdated() {
		const event = new CustomEvent('captureImages', {bubbles: true, composed: true});
		this.dispatchEvent(event);
	}

	sortObjectPropertiesRecursively(obj) {
		// If not an object or is null, return as is
		if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
			return obj;
		}
		const sortedObj = {};
		const keys = Object.keys(obj).sort();

		// Add each property in alphabetical order
		for (const key of keys) {
			// Recursively sort any nested objects
			sortedObj[key] = this.sortObjectPropertiesRecursively(obj[key]);
		}
		return sortedObj;
	}

	getShortNumber() {
		const spcSession = selectSpcSession(store.getState());
		const currentDecisions = spcToStoredDecisions(spcSession?.getDecisions());

		// TODO: central compile and restore config data for storage
		let configurationData = {
			decisions: getFilteredDecisions(currentDecisions),
			design: this.state.iomo.data.design,
			general: this.state.iomo.data.general,
			heads: this.state.iomo.data.heads,
		};
		// ensure a stable structure by sorting alphabetically
		configurationData = this.sortObjectPropertiesRecursively(configurationData);
		store.dispatch(fetchShortNumber({
			prefixLevels: ['ProductModel', 'Iomo'],
			data: JSON.stringify(
				{
					configurationData: configurationData
				}
			)
		}));
	}

	disconnectedCallback() {
		super.disconnectedCallback();
	}
	connectedCallback() {
		super.connectedCallback();
	}

	render() {
		if (!this.spcConfiguration) return null;

		return html`
			${when(this.showPersonalisation, () => html`
				<shared-specsheet-page class="page ${classMap({print: this.print})}" headLabel="${msg('Personalisation')}">
					<personalise-form></personalise-form>
				</shared-specsheet-page>
			`)}
			<shared-specsheet-page class="page" headLabel="${this.typeLabels[this.type]}">
				<family-overview></family-overview>
			</shared-specsheet-page>
			<shared-specsheet-page class="page" headLabel="${msg('Special features')}">
				<model-overview></model-overview>
			</shared-specsheet-page>
			<shared-specsheet-page class="page" headLabel="${msg('Specifications')}">
				<iomo-specsheet-layout-page></iomo-specsheet-layout-page>
			</shared-specsheet-page>
			<shared-specsheet-page class="page" headLabel="${msg('Tender Text')}">
				<iomo-specsheet-tender-text-page></iomo-specsheet-tender-text-page>
			</shared-specsheet-page>
			${repeat(this.groupedHeads,
				(headData, index) => 'head_' + index,
				(headData) => html`
					<shared-specsheet-page class="page" headLabel="${msg('Specifications')}">
						<iomo-specsheet-head-page .headData="${headData}"></iomo-specsheet-head-page>
					</shared-specsheet-page>
					${when(headData.head.type === 'luminaire', () => html`
						<shared-specsheet-page class="page" headLabel="${msg('Light')}">
							<iomo-specsheet-head-light-page .headData="${headData}"></iomo-specsheet-head-light-page>
						</shared-specsheet-page>
					`)}
				`)}
			<shared-specsheet-page class="page" headLabel="${msg('Drill Parameters')}">
				<iomo-specsheet-drill-page></iomo-specsheet-drill-page>
			</shared-specsheet-page>
		`;
	}
}
